import { useCallback, useEffect, useRef, useState } from 'react'

type Validadores<V> = { [K in keyof V]?: (v: V[K], todos: V) => string | null }

// Formulário mínimo para os drawers de SSO. `@mantine/form` não é dependência do
// frontend; a validação é a mesma função pura de ssoValidacao, avaliada a cada render.
export function useFormSimples<V extends { [K in keyof V]: string }>(
  inicial: () => V, validadores: Validadores<V>, opened: boolean, chave: unknown,
) {
  // `inicial` fecha sobre as props do render atual; o ref evita reset com valores velhos.
  const inicialRef = useRef(inicial)
  inicialRef.current = inicial
  const [values, setValues] = useState<V>(inicial)
  const [tocados, setTocados] = useState<Partial<Record<keyof V, boolean>>>({})
  const [doServidor, setDoServidor] = useState<Partial<Record<keyof V, string>>>({})

  const erros = {} as Record<keyof V, string | null>
  for (const k of Object.keys(values) as (keyof V)[]) {
    const f = validadores[k]
    erros[k] = f ? f(values[k], values) : null
  }
  const valido = (Object.keys(values) as (keyof V)[]).every((k) => erros[k] === null)

  const props = (k: keyof V) => ({
    value: values[k],
    error: doServidor[k] ?? (tocados[k] ? erros[k] : null) ?? undefined,
    onChange: (e: { currentTarget: { value: string } }) => {
      const v = e.currentTarget.value
      setValues((p) => ({ ...p, [k]: v }))
      setTocados((p) => ({ ...p, [k]: true }))
      setDoServidor((p) => ({ ...p, [k]: undefined }))
    },
  })

  const reset = useCallback(() => {
    setValues(inicialRef.current())
    setTocados({})
    setDoServidor({})
  }, [])

  // Abrir, fechar ou trocar o registro editado recomeça do valor inicial atual.
  useEffect(() => { reset() }, [opened, chave, reset])

  return {
    values, valido, props, reset,
    setErroServidor: (k: keyof V, msg: string) => setDoServidor((p) => ({ ...p, [k]: msg })),
    limpa: (k: keyof V) => setValues((p) => ({ ...p, [k]: '' as V[keyof V] })),
  }
}
